import { useEffect, useState } from 'react'
import type { OfferInfo } from '@/types/api'
import { offerResetAt, resetInTrueUTC, resetInViewerTime } from '@/utils/reset-time'
import { ResetTimeTag } from './ResetTimeTag'

/**
 * The reset tag for ONE offer, as shown on each tab of the offer switcher.
 *
 * Offers no longer share a cap day, so the boundary belongs next to the offer it governs rather
 * than only in the header — the header chip can only ever describe the tab currently selected,
 * which is exactly the information an affiliate deciding WHICH offer to run does not have yet.
 *
 * Presentation is `ResetTimeTag`, the same shell the header and admin-dashboard's `/cap` use. This
 * component only supplies the per-offer bits: which instant, and a tooltip naming the offer.
 */
export function OfferResetTag({
  offer,
  /** The server's own answer for the SELECTED offer, when this tag is rendering that one. */
  serverNextReset,
  showCountdown = true,
  compact = false,
  className = '',
}: {
  offer: OfferInfo
  serverNextReset?: string | null
  showCountdown?: boolean
  compact?: boolean
  className?: string
}) {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    // Only tick when a countdown is actually on screen — the tabs render these static, and a timer
    // per tab would re-render the whole switcher every second for numbers nobody is watching.
    if (!showCountdown) return
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [showCountdown])

  const resetAt = offerResetAt(offer, serverNextReset, now)

  return (
    <ResetTimeTag
      resetAt={resetAt}
      showCountdown={showCountdown}
      compact={compact}
      className={className}
      title={`${offer.name} resets at ${resetInTrueUTC(resetAt)} — ${resetInViewerTime(resetAt)} your time`}
    />
  )
}
